import { saveAs } from "file-saver";

/**
 * Escapes CSV values that contain commas, quotes, or newlines
 * Also mitigates CSV formula injection by prefixing dangerous characters
 */
const escapeCSV = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  const stringValue = String(value);

  // Mitigate CSV formula injection: prefix values starting with =, +, -, @, or \t
  // Excel/Sheets will treat these as text rather than formulas
  const needsFormulaProtection = /^[=+\-@\t]/.test(stringValue);
  const protectedValue = needsFormulaProtection
    ? `\t${stringValue}`
    : stringValue;

  if (
    protectedValue.includes(",") ||
    protectedValue.includes('"') ||
    protectedValue.includes("\n")
  ) {
    return `"${protectedValue.replace(/"/g, '""')}"`;
  }
  return protectedValue;
};

/**
 * Gets the supply/demand label for a team
 * @param {Object} team - Team object
 * @param {Object} groupTypes - Group types configuration
 * @param {Object} allocationConfig - Allocation config with supplyLabel and demandLabel
 * @returns {string} The structure type label (supply/demand) or empty string
 */
const getTeamTypeLabel = (team, groupTypes, allocationConfig) => {
  if (!team || !team.type || !groupTypes) {
    return "";
  }
  const groupType = groupTypes[team.type];
  if (!groupType) {
    return "";
  }
  const { demandLabel = "demand", supplyLabel = "supply" } =
    allocationConfig || {};
  return groupType.isSupply
    ? supplyLabel
    : groupType.isDemand
      ? demandLabel
      : "";
};

/**
 * Converts changelog data to CSV format and downloads it
 * @param {Object} changelogData - The changelog data object with summary and entries
 * @param {Object} dateRange - Date range object with from and to dates
 * @param {Object} workspaceConfig - Workspace configuration with groupTypes and allocation
 */
export const exportChangelogToCSV = (
  changelogData,
  dateRange,
  workspaceConfig
) => {
  if (!changelogData || !dateRange) {
    return;
  }

  const { entries } = changelogData;
  const lines = [];
  const groupTypes = workspaceConfig?.groupTypes || {};
  const allocationConfig = workspaceConfig?.allocation || {};

  // Header row
  lines.push("Category,Type,Entity Name,Structure Type,From,To,Change Date");

  // Helper function to get team/member name for CSV (plain text, no badges)
  const getEntityDisplayName = (entry) => {
    return entry.entityName || entry.entityId || "";
  };

  const getTeamDisplayName = (entry, field) => {
    if (field === "from") {
      return entry.fromName || entry.from || "";
    }
    if (field === "to") {
      return entry.toName || entry.to || "";
    }
    return "";
  };

  // Collect all entries from all categories
  const allEntries = [];

  // Structures (formerly Teams)
  (entries.teams || []).forEach((entry) => {
    const structureType = entry.entity
      ? getTeamTypeLabel(entry.entity, groupTypes, allocationConfig)
      : "";
    allEntries.push({
      category: "Structures",
      type: entry.type,
      entityName: getEntityDisplayName(entry),
      structureType,
      from: getTeamDisplayName(entry, "from"),
      to: getTeamDisplayName(entry, "to"),
      changeDate: entry.changeDate || "",
    });
  });

  // Members
  (entries.members || []).forEach((entry) => {
    allEntries.push({
      category: "Members",
      type: entry.type,
      entityName: getEntityDisplayName(entry),
      structureType: "", // Not applicable for members
      from: getTeamDisplayName(entry, "from"),
      to: getTeamDisplayName(entry, "to"),
      changeDate: entry.changeDate || "",
    });
  });

  // Allocations
  (entries.allocations || []).forEach((entry) => {
    allEntries.push({
      category: "Allocations",
      type: entry.type,
      entityName: getEntityDisplayName(entry),
      structureType: "", // Not applicable for allocations
      from: entry.fteChanged ? `FTE: ${entry.fromFte}` : "",
      to: entry.fteChanged ? `FTE: ${entry.toFte}` : "",
      changeDate: entry.changeDate || "",
    });
  });

  // Attributes
  (entries.attributes || []).forEach((entry) => {
    allEntries.push({
      category: "Attributes",
      type: entry.type,
      entityName: getEntityDisplayName(entry),
      structureType: "", // Not applicable for attributes
      from: "",
      to: "",
      changeDate: entry.changeDate || "",
    });
  });

  // Tags
  (entries.tags || []).forEach((entry) => {
    allEntries.push({
      category: "Tags",
      type: entry.type,
      entityName: getEntityDisplayName(entry),
      structureType: "", // Not applicable for tags
      from: "",
      to: "",
      changeDate: entry.changeDate || "",
    });
  });

  // Sort entries by change date, then by category
  allEntries.sort((a, b) => {
    if (a.changeDate !== b.changeDate) {
      return (a.changeDate || "").localeCompare(b.changeDate || "");
    }
    return a.category.localeCompare(b.category);
  });

  // Add all entries as rows
  allEntries.forEach((entry) => {
    const row = [
      escapeCSV(entry.category),
      escapeCSV(entry.type),
      escapeCSV(entry.entityName),
      escapeCSV(entry.structureType || ""),
      escapeCSV(entry.from),
      escapeCSV(entry.to),
      escapeCSV(entry.changeDate),
    ];
    lines.push(row.join(","));
  });

  // Convert to CSV string
  const csvContent = lines.join("\n");

  // Create blob and download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

  // Format filename: fromdate_todate_changelog.csv
  // Ensure from and to dates exist before calling replace
  if (!dateRange.from || !dateRange.to) {
    console.warn("Cannot export CSV: missing date range");
    return;
  }
  const fromDate = dateRange.from.replace(/-/g, "");
  const toDate = dateRange.to.replace(/-/g, "");
  const fileName = `${fromDate}_${toDate}_changelog.csv`;

  saveAs(blob, fileName);
};
